import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const HAS_EXPLICIT_OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const NAIVE_KUMA_TIME =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/;

/**
 * Kuma heartbeats arrive as naive UTC (`2026-09-14 19:41:28.708`).
 * Browsers parse that as local time, so CEST shows every check ~2 hours stale.
 * Treat offset-less datetimes as UTC; leave ISO/offset strings and Date/number alone.
 */
export function parseKumaDate(date: Date | string | number | null | undefined): Date {
  if (date instanceof Date) return date;
  if (typeof date === "number") return new Date(date);
  if (typeof date !== "string") return new Date(Number.NaN);
  const trimmed = date.trim();
  if (!HAS_EXPLICIT_OFFSET.test(trimmed)) {
    const naive = trimmed.match(NAIVE_KUMA_TIME);
    if (naive) {
      return new Date(`${naive[1]}T${naive[2]}Z`);
    }
  }
  return new Date(trimmed);
}

/**
 * Format date in Hungarian standard: YYYY. MM. DD. HH:mm:ss
 */
export function formatHungarianDateTime(date: Date | string | number): string {
  const d = parseKumaDate(date);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}

/**
 * Relative time in Hungarian (pl. "2 perce", "most")
 */
export function formatRelativeTimeHu(date: Date | string | number): string {
  const d = parseKumaDate(date);
  if (isNaN(d.getTime())) return "—";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);

  if (diffSec < 30) return "épp most";
  if (diffSec < 60) return `${diffSec} másodperce`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} perce`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} órája`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} napja`;
}
